import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControlLabel,
  Snackbar,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import AddIcon from "@mui/icons-material/Add";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import SearchOutlinedIcon from "@mui/icons-material/SearchOutlined";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import StorefrontOutlinedIcon from "@mui/icons-material/StorefrontOutlined";
import LocalPhoneOutlinedIcon from "@mui/icons-material/LocalPhoneOutlined";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
import TableRestaurantOutlinedIcon from "@mui/icons-material/TableRestaurantOutlined";
import AlternateEmailOutlinedIcon from "@mui/icons-material/AlternateEmailOutlined";
import axios from "axios";
import { useOutletContext } from "react-router-dom";

import { endPoint } from "../../global";
import { useAuth } from "../../context/AuthProvider";
import Loading from "../../components/Loading";
import ResponseAlert from "../../components/ResponseAlert";

const poppins = {
  fontFamily: "'Poppins', sans-serif",
};

const pageWrapper = {
  width: "100%",
  zIndex: 1,
  position: "relative",
  px: { xs: 1.5, md: 2.2 },
  py: { xs: 1.5, md: 2 },
};

const glassPanel = {
  position: "relative",
  overflow: "hidden",
  borderRadius: "20px",
  background:
    "linear-gradient(145deg, rgba(8,22,51,0.78), rgba(15,45,92,0.48))",
  border: "1px solid rgba(147,197,253,0.22)",
  backdropFilter: "blur(18px)",
  WebkitBackdropFilter: "blur(18px)",
  boxShadow: "0 16px 42px rgba(0,0,0,0.3)",
};

const tablePanel = {
  ...glassPanel,
  p: { xs: 1.2, md: 1.5 },
};

const actionBtn = (color) => ({
  ...poppins,
  fontWeight: 800,
  fontSize: "0.68rem",
  textTransform: "none",
  borderRadius: "9px",
  px: 1.15,
  py: 0.42,
  minWidth: 0,
  boxShadow: "none",

  ...(color === "blue" && {
    background: "linear-gradient(135deg, #1d4ed8, #2563eb)",
    color: "#fff",
    "&:hover": {
      background: "linear-gradient(135deg, #1e40af, #1d4ed8)",
      boxShadow: "0 6px 16px rgba(37,99,235,0.28)",
    },
  }),

  ...(color === "softBlue" && {
    color: "#93c5fd",
    backgroundColor: "rgba(96,165,250,0.14)",
    border: "1px solid rgba(147,197,253,0.25)",
    "&:hover": {
      backgroundColor: "rgba(96,165,250,0.22)",
    },
  }),
});

const searchInputSx = {
  "& .MuiOutlinedInput-root": {
    backgroundColor: "rgba(255,255,255,0.08)",
    color: "#fff",
    fontFamily: "'Poppins', sans-serif",
    fontSize: "0.78rem",
    borderRadius: "13px",

    "& fieldset": {
      borderColor: "rgba(147,197,253,0.18)",
    },

    "&:hover fieldset": {
      borderColor: "rgba(147,197,253,0.42)",
    },

    "&.Mui-focused fieldset": {
      borderColor: "#60a5fa",
    },
  },

  "& .MuiInputBase-input": {
    py: 1,
  },

  "& input::placeholder": {
    color: "rgba(219,234,254,0.55)",
    opacity: 1,
  },
};

const darkInput = {
  "& .MuiOutlinedInput-root": {
    backgroundColor: "rgba(255,255,255,0.07)",
    color: "#fff",
    fontFamily: "'Poppins', sans-serif",
    fontSize: "0.82rem",
    borderRadius: "13px",

    "& fieldset": {
      borderColor: "rgba(147,197,253,0.18)",
    },

    "&:hover fieldset": {
      borderColor: "rgba(147,197,253,0.42)",
    },

    "&.Mui-focused fieldset": {
      borderColor: "#60a5fa",
    },
  },

  "& .MuiInputLabel-root": {
    color: "rgba(219,234,254,0.58)",
    fontFamily: "'Poppins', sans-serif",
    fontSize: "0.8rem",
  },

  "& .MuiInputLabel-root.Mui-focused": {
    color: "#60a5fa",
  },

  "& .MuiInputBase-input": {
    py: 1.15,
  },

  "& .MuiFormHelperText-root": {
    fontFamily: "'Poppins', sans-serif",
  },
};

const gridSx = {
  border: "1px solid rgba(147,197,253,0.18)",
  backgroundColor: "rgba(3,10,26,0.42)",
  color: "#fff",
  borderRadius: "16px",
  overflow: "hidden",
  minWidth: 980,

  "& .MuiDataGrid-columnHeaders": {
    backgroundColor: "rgba(15,35,78,0.95)",
    borderBottom: "1px solid rgba(147,197,253,0.16)",
    minHeight: "42px !important",
    maxHeight: "42px !important",
  },

  "& .MuiDataGrid-columnHeader": {
    backgroundColor: "rgba(15,35,78,0.95)",
    minHeight: "42px !important",
    maxHeight: "42px !important",
  },

  "& .MuiDataGrid-columnHeaderTitle": {
    color: "#fff",
    fontWeight: 800,
    fontSize: "0.7rem",
    fontFamily: "'Poppins', sans-serif",
  },

  "& .MuiDataGrid-columnHeader .MuiSvgIcon-root": {
    color: "rgba(219,234,254,0.72)",
  },

  "& .MuiDataGrid-row": {
    borderBottom: "1px solid rgba(147,197,253,0.08)",
    fontFamily: "'Poppins', sans-serif",
    fontSize: "0.74rem",
    color: "rgba(255,255,255,0.86)",
    backgroundColor: "rgba(255,255,255,0.02)",
    minHeight: "46px !important",
    maxHeight: "46px !important",
    "&:hover": {
      backgroundColor: "rgba(96,165,250,0.08)",
    },
  },

  "& .MuiDataGrid-cell": {
    borderBottom: "none",
    color: "rgba(255,255,255,0.86)",
    minHeight: "46px !important",
    maxHeight: "46px !important",
    py: "4px",
  },

  "& .MuiDataGrid-footerContainer": {
    minHeight: "42px",
    backgroundColor: "rgba(15,35,78,0.82)",
    borderTop: "1px solid rgba(147,197,253,0.16)",
    color: "rgba(219,234,254,0.82)",
  },

  "& .MuiTablePagination-root": {
    color: "rgba(219,234,254,0.82)",
    fontFamily: "'Poppins', sans-serif",
    fontSize: "0.74rem",
  },

  "& .MuiTablePagination-selectIcon": {
    color: "#93c5fd",
  },

  "& .MuiIconButton-root": {
    color: "#93c5fd",
    p: 0.6,
  },

  "& .MuiDataGrid-filler": {
    backgroundColor: "rgba(15,35,78,0.95)",
  },

  "& .MuiDataGrid-scrollbarFiller": {
    backgroundColor: "rgba(15,35,78,0.95)",
  },
};

const emptyForm = {
  id: null,
  name: "",
  storeName: "",
  phoneNo: "",
  area: "",
  address: "",
  email: "",
  tableNo: "",
  isAdmin: false,
  isSuperAdmin: false,
};

const requiredFields = ["name", "storeName", "phoneNo"];

const normalizeBoolean = (value) => {
  return value === true || value === 1 || value === "1" || value === "true";
};

const getTokenString = (token) => {
  if (!token) return "";

  if (typeof token === "string") {
    return token.replace("Bearer ", "");
  }

  if (typeof token === "object") {
    return (
      token.token ||
      token.accessToken ||
      token.jwt ||
      token.value ||
      ""
    ).replace("Bearer ", "");
  }

  return "";
};

const decodeJwtPayload = (token) => {
  try {
    const cleanToken = getTokenString(token);

    if (!cleanToken) return null;

    const base64Url = cleanToken.split(".")[1];

    if (!base64Url) return null;

    let base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");

    while (base64.length % 4) {
      base64 += "=";
    }

    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split("")
        .map((char) => {
          return "%" + ("00" + char.charCodeAt(0).toString(16)).slice(-2);
        })
        .join(""),
    );

    return JSON.parse(jsonPayload);
  } catch (error) {
    return null;
  }
};

const findUserInContext = (value) => {
  if (!value) return null;

  if (Array.isArray(value)) {
    for (const item of value) {
      const found = findUserInContext(item);

      if (found) return found;
    }

    return null;
  }

  if (typeof value === "object") {
    if (
      value.id ||
      value.name ||
      value.isAdmin !== undefined ||
      value.isSuperAdmin !== undefined
    ) {
      return value;
    }

    if (value.user) return findUserInContext(value.user);
    if (value.data) return findUserInContext(value.data);
    if (value.profile) return findUserInContext(value.profile);
    if (value.auth) return findUserInContext(value.auth);
  }

  return null;
};

const InfoLine = ({ icon, label, value }) => (
  <Box
    sx={{
      display: "flex",
      alignItems: "center",
      gap: 1,
      minWidth: 0,
    }}
  >
    <Box sx={{ color: "#93c5fd", display: "flex" }}>{icon}</Box>

    <Box sx={{ minWidth: 0 }}>
      <Typography
        sx={{
          ...poppins,
          color: "rgba(219,234,254,0.55)",
          fontSize: "0.62rem",
          lineHeight: 1,
          mb: 0.28,
        }}
      >
        {label}
      </Typography>

      <Typography
        sx={{
          ...poppins,
          color: "#fff",
          fontSize: "0.76rem",
          fontWeight: 700,
          lineHeight: 1.2,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {value || "-"}
      </Typography>
    </Box>
  </Box>
);

const RoleChip = ({ row }) => {
  if (row.isSuperAdmin) {
    return (
      <Chip
        label="Super Admin"
        size="small"
        sx={{
          ...poppins,
          height: 24,
          fontSize: "0.64rem",
          fontWeight: 900,
          color: "#fbbf24",
          backgroundColor: "rgba(251,191,36,0.12)",
          border: "1px solid rgba(251,191,36,0.28)",
        }}
      />
    );
  }

  if (row.isAdmin) {
    return (
      <Chip
        label="Admin"
        size="small"
        sx={{
          ...poppins,
          height: 24,
          fontSize: "0.64rem",
          fontWeight: 900,
          color: "#93c5fd",
          backgroundColor: "rgba(96,165,250,0.12)",
          border: "1px solid rgba(147,197,253,0.28)",
        }}
      />
    );
  }

  return (
    <Chip
      label="User"
      size="small"
      sx={{
        ...poppins,
        height: 24,
        fontSize: "0.64rem",
        fontWeight: 900,
        color: "#6ee7b7",
        backgroundColor: "rgba(16,185,129,0.12)",
        border: "1px solid rgba(52,211,153,0.28)",
      }}
    />
  );
};

const UserMobileCard = ({ row, onEdit }) => (
  <Box
    sx={{
      ...glassPanel,
      p: 1.45,
      borderRadius: "18px",
    }}
  >
    <Box sx={{ display: "flex", justifyContent: "space-between", gap: 1.2 }}>
      <Box sx={{ minWidth: 0 }}>
        <Typography
          sx={{
            ...poppins,
            color: "#fff",
            fontWeight: 900,
            fontSize: "0.92rem",
            lineHeight: 1.15,
          }}
        >
          {row.name}
        </Typography>

        <Typography
          sx={{
            ...poppins,
            color: "rgba(219,234,254,0.62)",
            fontSize: "0.72rem",
            mt: 0.3,
          }}
        >
          {row.storeName}
        </Typography>
      </Box>

      <RoleChip row={row} />
    </Box>

    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: "1fr 1fr",
        gap: 1,
        mt: 1.4,
      }}
    >
      <InfoLine
        icon={<LocalPhoneOutlinedIcon fontSize="small" />}
        label="No HP"
        value={row.phoneNo}
      />

      <InfoLine
        icon={<LocationOnOutlinedIcon fontSize="small" />}
        label="Area"
        value={row.area}
      />

      <InfoLine
        icon={<TableRestaurantOutlinedIcon fontSize="small" />}
        label="Table"
        value={row.tableNo}
      />

      <InfoLine
        icon={<AlternateEmailOutlinedIcon fontSize="small" />}
        label="Email"
        value={row.email}
      />

      <Box sx={{ gridColumn: "1 / -1" }}>
        <InfoLine
          icon={<StorefrontOutlinedIcon fontSize="small" />}
          label="Alamat"
          value={row.address}
        />
      </Box>
    </Box>

    <Box
      sx={{
        display: "flex",
        justifyContent: "flex-end",
        mt: 1.4,
      }}
    >
      <Button
        onClick={() => onEdit(row)}
        startIcon={<EditOutlinedIcon />}
        sx={actionBtn("softBlue")}
      >
        Edit
      </Button>
    </Box>
  </Box>
);

export default function User() {
  const { token } = useAuth();
  const outletContext = useOutletContext();

  const outletUser = useMemo(() => {
    return findUserInContext(outletContext);
  }, [outletContext]);

  const tokenUser = useMemo(() => {
    return decodeJwtPayload(token);
  }, [token]);

  const localStorageUser = useMemo(() => {
    try {
      const storedUser =
        localStorage.getItem("user") ||
        localStorage.getItem("authUser") ||
        localStorage.getItem("profile");

      return storedUser ? JSON.parse(storedUser) : null;
    } catch (error) {
      return null;
    }
  }, []);

  const loggedUser = outletUser || tokenUser || localStorageUser;

  const currentUserIsSuperAdmin = normalizeBoolean(loggedUser?.isSuperAdmin);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const [users, setUsers] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  const [search, setSearch] = useState("");
  const [openDialog, setOpenDialog] = useState(false);
  const [mode, setMode] = useState("add");
  const [formData, setFormData] = useState(emptyForm);
  const [formErrors, setFormErrors] = useState({});

  const [response, setResponse] = useState("");
  const [success, setSuccess] = useState(true);
  const [openAlert, setOpenAlert] = useState(false);

  const [dialogAlert, setDialogAlert] = useState({
    open: false,
    message: "",
    severity: "error",
  });

  const showPageAlert = (message, severity = "success") => {
    setDialogAlert({
      open: false,
      message: "",
      severity,
    });

    setTimeout(() => {
      setDialogAlert({
        open: true,
        message,
        severity,
      });
    }, 0);
  };

  const fetchUsers = async () => {
    setIsLoading(true);

    try {
      const res = await axios.get(endPoint + "getUser", {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      setUsers(res.data.data || []);
    } catch (error) {
      showPageAlert(
        error.response?.data?.msg || "Gagal mengambil data user.",
        "error",
      );
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
    // eslint-disable-next-line
  }, []);

  const rows = useMemo(
    () =>
      users
        .filter((user) => {
          const userIsSuperAdmin = normalizeBoolean(user.isSuperAdmin);

          if (!currentUserIsSuperAdmin && userIsSuperAdmin) {
            return false;
          }

          return true;
        })
        .map((user) => ({
          id: user.id,
          name: user.name || "",
          storeName: user.storeName || "",
          phoneNo: user.phoneNo || "",
          area: user.area || "",
          address: user.address || "",
          email: user.email || "",
          tableNo: user.tableNo || "",
          isAdmin: normalizeBoolean(user.isAdmin),
          isSuperAdmin: normalizeBoolean(user.isSuperAdmin),
        })),
    [users, currentUserIsSuperAdmin],
  );

  const filteredRows = useMemo(() => {
    const keyword = search.toLowerCase().trim();

    if (!keyword) return rows;

    return rows.filter((row) =>
      [
        row.name,
        row.storeName,
        row.phoneNo,
        row.area,
        row.address,
        row.email,
        row.tableNo,
        row.isAdmin ? "admin" : "",
        row.isSuperAdmin ? "super admin" : "",
      ]
        .join(" ")
        .toLowerCase()
        .includes(keyword),
    );
  }, [rows, search]);

  const validateForm = () => {
    const errors = {};

    requiredFields.forEach((field) => {
      if (!String(formData[field] || "").trim()) {
        errors[field] = "Wajib diisi";
      }
    });

    if (formData.email && !/\S+@\S+\.\S+/.test(formData.email)) {
      errors.email = "Format email tidak valid";
    }

    setFormErrors(errors);

    return Object.keys(errors).length === 0;
  };

  const handleOpenAdd = () => {
    setMode("add");
    setFormData(emptyForm);
    setFormErrors({});
    setDialogAlert({
      open: false,
      message: "",
      severity: "error",
    });
    setOpenDialog(true);
  };

  const handleOpenEdit = (row) => {
    setMode("edit");
    setFormData({
      id: row.id,
      name: row.name || "",
      storeName: row.storeName || "",
      phoneNo: row.phoneNo || "",
      area: row.area || "",
      address: row.address || "",
      email: row.email || "",
      tableNo: row.tableNo || "",
      isAdmin: Boolean(row.isAdmin),
      isSuperAdmin: Boolean(row.isSuperAdmin),
    });
    setFormErrors({});
    setDialogAlert({
      open: false,
      message: "",
      severity: "error",
    });
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setDialogAlert({
      open: false,
      message: "",
      severity: "error",
    });
  };

  const handleChange = (event) => {
    const { name, value, type, checked } = event.target;

    setFormData((prev) => {
      if (type === "checkbox") {
        if (name === "isSuperAdmin") {
          return {
            ...prev,
            isSuperAdmin: checked,
            isAdmin: checked ? true : prev.isAdmin,
          };
        }

        if (name === "isAdmin") {
          return {
            ...prev,
            isAdmin: checked,
            isSuperAdmin: checked ? prev.isSuperAdmin : false,
          };
        }
      }

      return {
        ...prev,
        [name]: type === "checkbox" ? checked : value,
      };
    });

    setFormErrors((prev) => ({
      ...prev,
      [name]: "",
    }));
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      setSuccess(false);
      setResponse("Nama, nama toko, dan nomor HP wajib diisi.");
      setOpenAlert(true);
      return;
    }

    setIsLoading(true);

    try {
      const payloadData = {
        id: formData.id,
        name: formData.name,
        storeName: formData.storeName,
        phoneNo: formData.phoneNo,
        area: formData.area || "",
        address: formData.address || "",
        email: formData.email || "",
        tableNo: formData.tableNo || "",
      };

      if (currentUserIsSuperAdmin) {
        payloadData.isSuperAdmin = Boolean(formData.isSuperAdmin);
        payloadData.isAdmin = formData.isSuperAdmin
          ? true
          : Boolean(formData.isAdmin);
      }

      if (mode === "edit") {
        const res = await axios.put(endPoint + "updateUser", payloadData, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        setSuccess(true);
        setResponse(res.data?.msg || "User berhasil diperbarui.");
      } else {
        const payload = {
          ...payloadData,
          id: undefined,
        };

        const res = await axios.post(endPoint + "addUser", payload, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        setSuccess(true);
        setResponse(res.data?.msg || "User berhasil ditambahkan.");
      }

      setOpenDialog(false);
      setOpenAlert(true);
      await fetchUsers();
    } catch (error) {
      const message =
        error.response?.data?.msg ||
        (mode === "edit"
          ? "Gagal memperbarui user."
          : "Gagal menambahkan user.");

      setOpenDialog(false);
      setSuccess(false);
      setResponse(message);
      setOpenAlert(true);
    } finally {
      setIsLoading(false);
    }
  };

  const exportToExcel = () => {
    const cleanedData = rows.map((item) => ({
      nama: item.name,
      namaToko: item.storeName,
      phoneNo: item.phoneNo,
      area: item.area,
      address: item.address,
      email: item.email,
      tableNo: item.tableNo,
      isAdmin: item.isAdmin ? 1 : 0,
      isSuperAdmin: item.isSuperAdmin ? 1 : 0,
    }));

    const ws = XLSX.utils.json_to_sheet(cleanedData);
    const wb = XLSX.utils.book_new();

    XLSX.utils.book_append_sheet(wb, ws, "DataUser");

    const buf = XLSX.write(wb, {
      bookType: "xlsx",
      type: "array",
    });

    saveAs(
      new Blob([buf], {
        type: "application/octet-stream",
      }),
      "Data-User.xlsx",
    );
  };

  const columns = [
    {
      field: "name",
      headerName: "Nama",
      minWidth: 165,
      flex: 1,
    },
    {
      field: "storeName",
      headerName: "Nama Toko",
      minWidth: 170,
      flex: 1,
    },
    {
      field: "phoneNo",
      headerName: "No HP",
      minWidth: 135,
      flex: 0.8,
    },
    {
      field: "area",
      headerName: "Area",
      minWidth: 120,
      flex: 0.75,
    },
    {
      field: "tableNo",
      headerName: "Table",
      minWidth: 80,
      flex: 0.45,
    },
    {
      field: "email",
      headerName: "Email",
      minWidth: 170,
      flex: 1,
    },
    {
      field: "role",
      headerName: "Role",
      minWidth: 130,
      flex: 0.65,
      sortable: false,
      renderCell: (params) => <RoleChip row={params.row} />,
    },
    {
      field: "action",
      headerName: "Aksi",
      minWidth: 90,
      flex: 0.45,
      sortable: false,
      renderCell: (params) => (
        <Button
          onClick={() => handleOpenEdit(params.row)}
          startIcon={<EditOutlinedIcon />}
          sx={actionBtn("softBlue")}
        >
          Edit
        </Button>
      ),
    },
  ];

  return (
    <Box sx={pageWrapper}>
      <Box sx={{ mb: 1.7 }}>
        <Typography
          sx={{
            ...poppins,
            fontWeight: 900,
            fontSize: { xs: "1.35rem", md: "1.65rem" },
            color: "#fff",
            lineHeight: 1.15,
            textShadow: "0 0 22px rgba(96,165,250,0.45)",
          }}
        >
          Manajemen User
        </Typography>

        <Typography
          sx={{
            ...poppins,
            color: "rgba(219,234,254,0.72)",
            fontSize: "0.78rem",
            mt: 0.4,
          }}
        >
          Kelola data user, toko, area, dan hak akses admin
        </Typography>
      </Box>

      <Box sx={tablePanel}>
        <Box
          sx={{
            position: "relative",
            zIndex: 1,
            display: "flex",
            justifyContent: "space-between",
            alignItems: { xs: "stretch", sm: "center" },
            flexDirection: { xs: "column", sm: "row" },
            gap: 1,
            mb: 1.1,
          }}
        >
          <Box>
            <Typography
              sx={{
                ...poppins,
                color: "#fff",
                fontWeight: 900,
                fontSize: "0.9rem",
              }}
            >
              Data User
            </Typography>

            <Typography
              sx={{
                ...poppins,
                color: "rgba(219,234,254,0.58)",
                fontSize: "0.68rem",
                mt: 0.2,
              }}
            >
              Menampilkan {filteredRows.length} dari {rows.length} user
            </Typography>
          </Box>

          <Box
            sx={{
              display: "flex",
              gap: 1,
              flexDirection: { xs: "column", sm: "row" },
            }}
          >
            <Button
              onClick={exportToExcel}
              startIcon={<FileDownloadIcon />}
              sx={{
                ...poppins,
                color: "#fff",
                textTransform: "none",
                fontWeight: 800,
                fontSize: "0.74rem",
                border: "1px solid rgba(147,197,253,0.25)",
                background: "rgba(96,165,250,0.14)",
                borderRadius: "12px",
                px: 1.6,
                py: 0.75,
                "&:hover": {
                  background: "rgba(96,165,250,0.22)",
                },
              }}
            >
              Export Excel
            </Button>

            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleOpenAdd}
              sx={actionBtn("blue")}
            >
              Tambah User
            </Button>
          </Box>
        </Box>

        <Box
          sx={{
            mb: 1.2,
            display: "flex",
            alignItems: "center",
            gap: 1,
          }}
        >
          <Box sx={{ color: "#93c5fd", display: "flex" }}>
            <SearchOutlinedIcon fontSize="small" />
          </Box>

          <TextField
            fullWidth
            size="small"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Cari nama, nama toko, no HP, area, email, table, atau role..."
            sx={searchInputSx}
          />
        </Box>

        {isMobile ? (
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "1fr",
              gap: 1,
            }}
          >
            {filteredRows.map((row) => (
              <UserMobileCard key={row.id} row={row} onEdit={handleOpenEdit} />
            ))}
          </Box>
        ) : (
          <Box
            sx={{
              width: "100%",
              overflowX: "auto",
              borderRadius: "16px",
            }}
          >
            <DataGrid
              rows={filteredRows}
              columns={columns}
              loading={isLoading}
              sx={gridSx}
              autoHeight
              rowHeight={46}
              columnHeaderHeight={42}
              disableRowSelectionOnClick
              pageSizeOptions={[5, 10, 25, 50]}
              initialState={{
                pagination: {
                  paginationModel: {
                    pageSize: 10,
                    page: 0,
                  },
                },
              }}
            />
          </Box>
        )}
      </Box>

      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        fullWidth
        maxWidth="sm"
        fullScreen={isMobile}
        PaperProps={{
          sx: {
            background: "linear-gradient(180deg, #091b3d 0%, #060f24 100%)",
            border: "1px solid rgba(255,255,255,0.1)",
            borderRadius: isMobile ? 0 : "20px",
          },
        }}
      >
        <DialogTitle
          sx={{
            ...poppins,
            fontWeight: 900,
            color: "#fff",
            fontSize: "1rem",
            py: 1.5,
            borderBottom: "1px solid rgba(255,255,255,0.08)",
          }}
        >
          {mode === "edit" ? "Edit User" : "Tambah User Baru"}
        </DialogTitle>

        <DialogContent
          dividers
          sx={{
            borderColor: "rgba(255,255,255,0.08)",
            pt: 2,
          }}
        >
          {[
            { label: "Nama", name: "name", required: true },
            { label: "Nama Toko", name: "storeName", required: true },
            { label: "No HP", name: "phoneNo", required: true },
            { label: "Area", name: "area", required: false },
            { label: "Alamat", name: "address", required: false },
            { label: "Email", name: "email", required: false },
            { label: "Nomor Meja", name: "tableNo", required: false },
          ].map(({ label, name, required }) => (
            <TextField
              key={name}
              fullWidth
              required={required}
              margin="dense"
              label={label}
              name={name}
              value={formData[name]}
              onChange={handleChange}
              error={Boolean(formErrors[name])}
              helperText={formErrors[name] || ""}
              sx={darkInput}
            />
          ))}

          <Box
            sx={{
              mt: 1,
              display: "flex",
              gap: 1,
              flexDirection: { xs: "column", sm: "row" },
            }}
          >
            {currentUserIsSuperAdmin && (
              <>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.isAdmin}
                      onChange={handleChange}
                      name="isAdmin"
                      sx={{
                        color: "rgba(96,165,250,0.5)",
                        "&.Mui-checked": { color: "#60a5fa" },
                      }}
                    />
                  }
                  label={
                    <Typography
                      sx={{
                        ...poppins,
                        color: "rgba(219,234,254,0.72)",
                        fontSize: "0.82rem",
                      }}
                    >
                      Admin
                    </Typography>
                  }
                />

                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.isSuperAdmin}
                      onChange={handleChange}
                      name="isSuperAdmin"
                      sx={{
                        color: "rgba(96,165,250,0.5)",
                        "&.Mui-checked": { color: "#60a5fa" },
                      }}
                    />
                  }
                  label={
                    <Typography
                      sx={{
                        ...poppins,
                        color: "rgba(219,234,254,0.72)",
                        fontSize: "0.82rem",
                      }}
                    >
                      Super Admin
                    </Typography>
                  }
                />
              </>
            )}
          </Box>
        </DialogContent>

        <DialogActions
          sx={{
            px: 2,
            py: 1.4,
            borderTop: "1px solid rgba(255,255,255,0.08)",
            gap: 1,
            flexDirection: { xs: "column", sm: "row" },
          }}
        >
          <Button
            onClick={handleCloseDialog}
            fullWidth={isMobile}
            sx={{
              ...poppins,
              color: "rgba(186,230,253,0.8)",
              textTransform: "none",
              border: "1px solid rgba(255,255,255,0.15)",
              borderRadius: "11px",
              px: 1.8,
              py: 0.85,
              fontWeight: 800,
              fontSize: "0.78rem",
              "&:hover": {
                backgroundColor: "rgba(255,255,255,0.05)",
              },
            }}
          >
            Batal
          </Button>

          <Button
            onClick={handleSubmit}
            variant="contained"
            fullWidth={isMobile}
            sx={{
              ...poppins,
              background: "linear-gradient(135deg, #1d4ed8, #2563eb)",
              borderRadius: "11px",
              textTransform: "none",
              fontWeight: 800,
              fontSize: "0.78rem",
              py: 0.85,
              "&:hover": {
                background: "linear-gradient(135deg, #1e40af, #1d4ed8)",
              },
            }}
          >
            {mode === "edit" ? "Simpan Perubahan" : "Simpan"}
          </Button>
        </DialogActions>
      </Dialog>

      <Loading open={isLoading} />

      <ResponseAlert
        response={response}
        success={success}
        openAlert={openAlert}
        setOpenAlert={setOpenAlert}
      />

      <Snackbar
        open={dialogAlert.open}
        autoHideDuration={3000}
        onClose={() =>
          setDialogAlert((prev) => ({
            ...prev,
            open: false,
          }))
        }
        anchorOrigin={{
          vertical: "top",
          horizontal: "center",
        }}
        sx={{
          zIndex: 999999,
        }}
      >
        <Alert
          severity={dialogAlert.severity}
          variant="filled"
          onClose={() =>
            setDialogAlert((prev) => ({
              ...prev,
              open: false,
            }))
          }
          sx={{
            ...poppins,
            fontWeight: 800,
            borderRadius: "12px",
            boxShadow: "0 14px 40px rgba(0,0,0,0.38)",
          }}
        >
          {dialogAlert.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
